export const getBrowserInfo = (): { browserType: string; browserVersion: string } => {
  const ua = navigator.userAgent;

  // Check for Edge specifically first, as it can appear with different patterns
  const edgeMatch = ua.match(/Edge\/([\d.]+)/) || ua.match(/Edg\/([\d.]+)/);
  if (edgeMatch) {
    return { browserType: 'edge', browserVersion: edgeMatch[1] };
  }

  const browserMatchers = [
    { regex: /Chrome\/([\d.]+)/, name: 'chrome', exclude: /Edg\/|OPR\// },
    { regex: /Firefox\/([\d.]+)/, name: 'firefox' },
    { regex: /Version\/([\d.]+).*Safari/, name: 'safari', exclude: /Chrome\/|Edg\/|OPR\// },
  ];

  const matchedBrowser = browserMatchers.find(({ regex, exclude }) => {
    const match = ua.match(regex);
    return match && (!exclude || !exclude.test(ua));
  });

  if (matchedBrowser) {
    const match = ua.match(matchedBrowser.regex);
    return { browserType: matchedBrowser.name, browserVersion: match ? match[1] : '' };
  }

  return { browserType: 'other', browserVersion: '' };
};

/**
 * Detects iPadOS 13+ devices which masquerade as Macintosh in userAgent
 * but can be identified by their touch support.
 *
 * iPadOS 13+ changed its user-agent to report as "Macintosh" to get desktop
 * versions of websites, but it can be distinguished by having touch support.
 *
 * This is useful for features that need to treat iPads as tablets even though
 * they report as desktop devices (e.g., disabling beforeunload prompts which
 * are unreliable on mobile/tablet operating systems).
 *
 * @returns true if running on iPadOS 13 or later
 *
 * @example
 * ```typescript
 * if (isIPadOS13Plus()) {
 *   // Disable beforeunload prompts for iPad
 *   return;
 * }
 * ```
 */
export const isIPadOS13Plus = (): boolean =>
  navigator.userAgent.includes('Macintosh') &&
  typeof navigator.maxTouchPoints === 'number' &&
  navigator.maxTouchPoints > 1;

/**
 * Returns the operating system of the device.
 * @returns 'iOS' | 'android' | 'other'
 */
export const getOS = (): string => {
  const { userAgent } = navigator;

  // iOS devices (iPhone, iPad, iPod)
  if (/iPad|iPhone|iPod/i.test(userAgent) || isIPadOS13Plus()) {
    return 'iOS';
  }

  // Android devices
  if (/Android/i.test(userAgent)) {
    return 'android';
  }

  return 'other';
};

/**
 * Returns the form factor of the device (mobile/tablet/desktop).
 * Primarily uses user agent, screen size helps validate edge cases.
 * @returns 'mobile' | 'tablet' | 'desktop'
 */
export const getDeviceType = (): string => {
  const { userAgent } = navigator;
  const screenWidth = window.screen?.width || 0;

  // Tablets: All tablets regardless of OS
  // User agent is primary, screen size helps with Android edge case
  if (
    /iPad/i.test(userAgent) ||
    isIPadOS13Plus() ||
    /(tablet|playbook|silk)/i.test(userAgent) ||
    (/Android/i.test(userAgent) && (!/Mobile/i.test(userAgent) || screenWidth >= 600))
  ) {
    return 'tablet';
  }

  // Mobile: All phones regardless of OS
  // User agent is primary, screen size helps confirm it's not a tablet
  if (
    /iPhone|iPod/i.test(userAgent) ||
    (/Android/i.test(userAgent) && /Mobile/i.test(userAgent) && screenWidth < 600) ||
    /Mobile|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      userAgent,
    )
  ) {
    return 'mobile';
  }

  // Desktop (or anything that doesn't match mobile/tablet patterns)
  return 'desktop';
};

/**
 * Detects iOS devices including iPhone, iPad, and iPod touch.
 * This includes iPadOS 13+ devices which masquerade as Macintosh.
 *
 * @returns true if running on any iOS device (iPhone, iPad, iPod)
 *
 */

export const isTabletDevice = (): boolean => getDeviceType() === 'tablet';
export const isMobileDevice = (): boolean => getDeviceType() === 'mobile';
export const isDesktopDevice = (): boolean => getDeviceType() === 'desktop';
export const isAndroidDevice = (): boolean => getOS() === 'android';
export const isIOSDevice = (): boolean => getOS() === 'iOS';
